import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useModal } from '@context/ModalsContext'

import { Body2 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Badge } from '@ui/components/Badge/Badge'
import { Button } from '@ui/components/Button'
import { Link } from '@ui/components/Link'
import { RichIcon } from '@ui/components/RichIcon'
import { CheckIcon } from '@ui/icons/Check.icon'
import { ClockIcon } from '@ui/icons/Clock.icon'
import { InfoIcon } from '@ui/icons/Info.icon'
import { IconProps } from '@ui/icons/types'
import { ExpandableRow } from '@ui/v2/Table/Table'

import { linkify } from '@utils/linkify'

import { SubscriptionBundleDto } from '@model/SubscriptionBundleDto'
import { SubscriptionCartItem } from '@model/SubscriptionCart'
import { SubscriptionGroupDto } from '@model/SubscriptionGroupDto'
import { SubscriptionRequestState } from '@model/SubscriptionRequestState'
import { SubscriptionRequestTypeEnum } from '@model/SubscriptionRequestTypeEnum'
import {
  Subscription,
  isSubscriptionActive,
  isSubscriptionImmediateAndPending,
  isSubscriptionPending,
} from '@model/SubscriptionTypes'
import {
  SubscriptionRequestType,
  UpdateSubscriptionRequest,
} from '@model/UpdateSubscriptionRequest'

import { useScrollToElementIds } from '@hooks/useScrollToElementIds'

import { Box } from '@shared/components/Box'
import { InformationModal as SharedInformationModal } from '@shared/modals/Information.modal'
import { ProductServiceModal } from '@shared/modals/ProductService.modal'

import { PriceRange } from '../SubscriptionsPriceRange'
import {
  SubscriptionGroupProductPrice,
  SubscriptionGroupRenderProductAction,
  useModals,
} from './SubscriptionGroupSubscription'

interface SubscriptionGroupStatusProps {
  isCollapsed: boolean
  setIsCollapsed(visible: boolean): void
  hasActiveSubscriptionRequest(): boolean
  hasPendingSubscriptionRequest(): boolean
  hasPendingUnsubscriptionRequest(): boolean
  hasProcessSubscriptionRequest(): boolean
  hasDeductingFundOrInProcessSubscriptionRequest(): boolean
  isSubscribedToBundle: boolean
  onSetActivePackageModal(): void
  isCancellationRequested(): boolean
  isActiveAndCancellationRequested(): boolean
}

const SubscriptionGroupStatus: React.FC<SubscriptionGroupStatusProps> = (props) => {
  const { setIsCollapsed, hasActiveSubscriptionRequest, hasPendingSubscriptionRequest } = props
  const { hasPendingUnsubscriptionRequest, hasProcessSubscriptionRequest } = props
  const { hasDeductingFundOrInProcessSubscriptionRequest, isSubscribedToBundle } = props
  const { onSetActivePackageModal, isCollapsed } = props
  const { isCancellationRequested, isActiveAndCancellationRequested } = props

  const { t } = useTranslation()

  switch (true) {
    case hasActiveSubscriptionRequest():
      return (
        <Badge type='green' contrast='low' size='sm'>
          {t('Subscriptions.Active')}
        </Badge>
      )
    case isSubscribedToBundle:
      return <ActiveViaPackage onSetActivePackageModal={onSetActivePackageModal} />
    case hasProcessSubscriptionRequest():
      return isCollapsed ? null : (
        <Badge type='blue' contrast='low' size='sm'>
          {t('Market Data.Processing')}
        </Badge>
      )
    case hasPendingUnsubscriptionRequest():
      return (
        <Badge type='blue' contrast='low' size='sm'>
          {t('Subscriptions.Pending')}
        </Badge>
      )
    case hasDeductingFundOrInProcessSubscriptionRequest():
      return isCollapsed ? null : (
        <Badge type='yellow' contrast='low' size='sm'>
          {t('Subscriptions.Processing')}
        </Badge>
      )
    case isActiveAndCancellationRequested():
      return (
        <Badge type='blue' contrast='low' size='sm'>
          {t('Subscriptions.Pending')}
        </Badge>
      )
    case isCancellationRequested():
      return (
        <Badge type='blue' contrast='low' size='sm'>
          {t('Subscriptions.Pending Cancellation')}
        </Badge>
      )
    case hasPendingSubscriptionRequest():
      return (
        <Badge type='blue' contrast='low' size='sm'>
          {t('Subscriptions.Pending')}
        </Badge>
      )

    default:
      return isCollapsed ? null : (
        <Button.Primary
          size='sm'
          onClick={() => {
            setIsCollapsed(true)
          }}
        >
          {t('Subscriptions.Subscribe')}
        </Button.Primary>
      )
  }
}

interface ActiveViaPackageProps {
  onSetActivePackageModal(): void
}

export const ActiveViaPackage: React.FC<ActiveViaPackageProps> = ({ onSetActivePackageModal }) => {
  const { t } = useTranslation()

  return (
    <Box flex align='center'>
      <Badge type='blue' contrast='low' size='sm'>
        {t('Subscriptions.Active Via Bundle')}
      </Badge>
      <Box ml='x4'>
        <ActionIcon icon={InfoIcon} size='x20' onClick={onSetActivePackageModal} />
      </Box>
    </Box>
  )
}

interface SubscriptionGroupProps {
  subscribedBundles: SubscriptionBundleDto[]
  subscriptionGroup: SubscriptionGroupDto
  onUpdateSubscription(data: UpdateSubscriptionRequest): void
  onAddCartItem(
    subscriptionDetailId: string,
    subscriptionRequestTypeId: SubscriptionRequestType
  ): void
  scrollToCart?: () => void
  nonActiveSubscriptions: SubscriptionCartItem[]
}

interface ProductStatusIconProps extends IconProps {
  subscription?: Subscription
}

export const ProductStatusIcon: React.FC<ProductStatusIconProps> = (props) => {
  const { subscription } = props

  if (subscription) {
    if (isSubscriptionActive(subscription)) {
      return <RichIcon icon={{ icon: CheckIcon, color: 'text-success-high-em' }} size='x20' />
    } else if (isSubscriptionPending(subscription.subscriptionRequest)) {
      return <RichIcon icon={{ icon: ClockIcon, color: 'text-warning-high-em' }} size='x20' />
    }
  }

  return null
}

export const isProductStatus = (subscription?: Subscription) => {
  return (
    isSubscriptionActive(subscription) || isSubscriptionPending(subscription?.subscriptionRequest)
  )
}

export const SubscriptionGroup: React.FC<SubscriptionGroupProps> = (props) => {
  const { subscriptionGroup, subscribedBundles, onUpdateSubscription } = props
  const { onAddCartItem, scrollToCart, nonActiveSubscriptions } = props

  const { t } = useTranslation()
  const { scrollIntoView } = useScrollToElementIds()

  const [isCollapsed, setIsCollapsed] = useState(false)
  const isSubscribedToBundle = !!subscribedBundles.filter(
    (bundle) => !!bundle.bundledGroups.filter((bg) => bg.id === subscriptionGroup.id).length
  ).length

  const activePackageInfo = useModal(SharedInformationModal, {
    title: t('Market Data.Included in Active Package'),
    secondaryLabel: t('Got It'),
    description: () => (
      <p className='text'>
        {t('Market Data.The product is already included in your active package subscription')}
      </p>
    ),
  })
  const modals = useModals()

  const hasActiveSubscriptionRequest = () => {
    return !!subscriptionGroup.subscriptions.find(
      (subscription) => subscription.activeSubscriptionRequest
    )
  }

  const hasPendingSubscriptionRequest = () => {
    return !!subscriptionGroup.subscriptions.find(
      (subscription) =>
        subscription.subscriptionRequest?.state?.name === SubscriptionRequestState.Pending &&
        subscription.subscriptionRequest?.type?.name ===
          SubscriptionRequestTypeEnum.AddNewSubscription
    )
  }

  const hasProcessSubscriptionRequest = () => {
    return !!subscriptionGroup.subscriptions.find((subscription) =>
      isSubscriptionImmediateAndPending(subscription.subscriptionRequest)
    )
  }

  const hasPendingUnsubscriptionRequest = () => {
    return !!subscriptionGroup.subscriptions.find(
      (subscription) =>
        subscription.subscriptionRequest?.state?.name === SubscriptionRequestState.Pending &&
        subscription.subscriptionRequest?.type?.name ===
          SubscriptionRequestTypeEnum.CancelExistingSubscription
    )
  }

  const hasDeductingFundOrInProcessSubscriptionRequest = () => {
    return !!subscriptionGroup.subscriptions.find(
      (subscription) =>
        subscription.subscriptionRequest?.state?.name === SubscriptionRequestState.DeductingFunds ||
        subscription.subscriptionRequest?.state?.name === SubscriptionRequestState.InProcess
    )
  }

  const isCancellationRequested = () => {
    return !!subscriptionGroup.subscriptions.find(
      (subscription) =>
        (subscription.subscriptionRequest?.state?.name === SubscriptionRequestState.Pending &&
          subscription.subscriptionRequest?.type?.name ===
            SubscriptionRequestTypeEnum.CancelExistingSubscription) ||
        subscription.subscriptionRequest?.state?.name === SubscriptionRequestState.InProcessCancel
    )
  }

  const isActiveAndCancellationRequested = () => {
    return !!subscriptionGroup.subscriptions.find(
      (subscription) =>
        (subscription?.activeSubscriptionRequest?.state?.name === SubscriptionRequestState.Active &&
          subscription.subscriptionRequest?.state?.name === SubscriptionRequestState.Pending &&
          subscription.subscriptionRequest?.type?.name ===
            SubscriptionRequestTypeEnum.CancelExistingSubscription) ||
        subscription.subscriptionRequest?.state?.name === SubscriptionRequestState.InProcessCancel
    )
  }

  const productServiceModal = useModal(ProductServiceModal)

  const handleOnSubscriptionGroupSelected = (group: SubscriptionGroupDto) => {
    productServiceModal.open({
      data: {
        name: group?.name,
        imageUrl: (group as any)?.imageUrl,
        imageUrlMobile: (group as any)?.imageUrlMobile,
        description: (group as any)?.description,
        url: (group as any)?.url,
      },
    })
  }

  const toggleIsCollapsed = (subscriptionId: string) => {
    if (!isCollapsed) {
      scrollIntoView([`market-data-subscription-${subscriptionId}`])
    }
    setIsCollapsed((prev) => !prev)
  }

  const id = subscriptionGroup.id.toString()
  const productPackages = subscriptionGroup.subscriptions.map((pkg) => ({
    ...pkg,
    id: pkg.id.toString(),
  }))
  const isExpanded = isCollapsed

  return (
    <ExpandableRow
      row={{
        ...subscriptionGroup,
        id,
        subRows: productPackages,
      }}
      isExpanded={isCollapsed}
      hasTruncate={false}
      onToggle={() => toggleIsCollapsed(id)}
      renderParent={(p) => {
        return [
          {
            value: (
              <Box flex direction='column' gap='x4'>
                <Body2 medium>
                  <Link
                    to='#'
                    onClick={(event) => {
                      event.preventDefault()
                      handleOnSubscriptionGroupSelected(subscriptionGroup)
                    }}
                  >
                    {p?.name}
                  </Link>
                </Body2>
                {p?.subName && (
                  <Body2 wrap='wrap' color='text-med-em'>
                    {p.subName}
                  </Body2>
                )}
              </Box>
            ),
            align: 'left',
          },
          {
            value: !isExpanded ? (
              <Body2>
                <PriceRange minAmount={p?.minAmount} maxAmount={p?.maxAmount} />
              </Body2>
            ) : undefined,
            align: 'left',
          },
          {
            value: (
              <SubscriptionGroupStatus
                hasActiveSubscriptionRequest={hasActiveSubscriptionRequest}
                hasPendingSubscriptionRequest={hasPendingSubscriptionRequest}
                hasPendingUnsubscriptionRequest={hasPendingUnsubscriptionRequest}
                hasDeductingFundOrInProcessSubscriptionRequest={
                  hasDeductingFundOrInProcessSubscriptionRequest
                }
                hasProcessSubscriptionRequest={hasProcessSubscriptionRequest}
                isActiveAndCancellationRequested={isActiveAndCancellationRequested}
                isCollapsed={isCollapsed}
                setIsCollapsed={setIsCollapsed}
                isSubscribedToBundle={isSubscribedToBundle}
                isCancellationRequested={isCancellationRequested}
                onSetActivePackageModal={activePackageInfo.open}
              />
            ),
            align: 'center',
          },
        ]
      }}
      renderSubRow={(subscription) => {
        return [
          {
            value: (
              <Box flex direction='row' gap='x4'>
                {isProductStatus(subscription) && (
                  <Box>
                    <ProductStatusIcon subscription={subscription} />
                  </Box>
                )}
                <Box pl='x16' flex direction='column' gap='x4'>
                  <Body2 medium>{subscription.name}</Body2>
                  {subscription?.description && (
                    <Body2 wrap='wrap' color='text-med-em'>
                      {linkify(subscription?.description)}
                    </Body2>
                  )}
                </Box>
              </Box>
            ),
            align: 'left',
          },
          {
            value: (
              <Box flex direction='row' gap='x4' align='center'>
                <SubscriptionGroupProductPrice
                  {...modals}
                  subscription={subscription}
                  scrollToCart={scrollToCart}
                  isSubscribedToBundle={isSubscribedToBundle}
                  onUpdateSubscription={onUpdateSubscription}
                  isCollapsed={isCollapsed}
                  onAddCartItem={onAddCartItem}
                  nonActiveSubscriptions={nonActiveSubscriptions}
                  key={subscription.id}
                />
              </Box>
            ),
            align: 'left',
          },
          {
            value: (
              <SubscriptionGroupRenderProductAction
                {...modals}
                subscription={subscription}
                scrollToCart={scrollToCart}
                isSubscribedToBundle={isSubscribedToBundle}
                onUpdateSubscription={onUpdateSubscription}
                isCollapsed={isCollapsed}
                onAddCartItem={onAddCartItem}
                nonActiveSubscriptions={nonActiveSubscriptions}
                key={subscription.id}
              />
            ),
            align: 'center',
          },
        ]
      }}
    />
  )
}
